import {inject, Injectable} from '@angular/core';
import {ActivatedRouteSnapshot, RouterStateSnapshot, UrlTree, Router, CanActivateFn} from '@angular/router';
import { Observable } from 'rxjs';
import {AuthenticationService} from './authentication.service';
import {map, switchMap, take} from 'rxjs/operators';
import {MediaService} from './media.service';
import {DeviceDetectorService} from 'ngx-device-detector';
import {mapMbSharedSubPath, normalizeRoutePath} from './mb-shared-subpaths';

@Injectable({
  providedIn: 'root'
})
class AuthenticationGuardService  {
  constructor(private authService: AuthenticationService, private router: Router, private mediaService: MediaService,
              private deviceService: DeviceDetectorService) { }
  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
    // const path = route.routeConfig?.path;
    // console.log({path});
    const urlTree = this.router.parseUrl(state.url);
    urlTree.queryParams = {};
    const normalizedPath = normalizeRoutePath(state.url);
    const isMobile = !this.deviceService.isDesktop() && !this.deviceService.isTablet();
    const isMobileMbRoute = normalizedPath.startsWith('/media-blast-mobile');
    const isMbRoute = normalizedPath.startsWith('/media-blast') && !isMobileMbRoute;
    const isUpgradeLogin = normalizedPath === '/upgrade-login';
    const shouldKeepMbReturnUrl = isMobileMbRoute &&
      !['/media-blast-mobile', '/media-blast-mobile/login'].includes(normalizedPath);
    const mobileLoginRedirect = (): UrlTree => {
      if (shouldKeepMbReturnUrl) {
        return this.router.createUrlTree(['/media-blast-mobile/login'], { queryParams: { returnUrl: state.url }});
      }
      return this.router.createUrlTree(['/media-blast-mobile/login']);
    };
    const loginRedirect = (): UrlTree => {
      return this.router.createUrlTree(['/login'], { queryParams: { returnUrl: state.url }});
    };
    const unauthRedirect = (): UrlTree => {
      return (isMobile && (isMobileMbRoute || isUpgradeLogin)) ? mobileLoginRedirect() : loginRedirect();
    };
    if (!this.mediaService.accessToken) {
      return unauthRedirect();
    }
    const decide = (): boolean | UrlTree => {
      const advertiserModules = this.mediaService.userAdvertiser?.modules || this.mediaService.selectedAdvertiser?.modules;
      const isMediaBlastUser = !this.mediaService.isAdminRole && !!advertiserModules?.mediablast;
      if (!isMobile) {
        if (isMobileMbRoute) {
          return this.router.parseUrl(mapMbSharedSubPath(state.url, '/media-blast-mobile', '/media-blast') || '/media-blast');
        }
        return true;
      }
      if (isMobileMbRoute) {
        return isMediaBlastUser ? true : this.router.parseUrl('/notDesktop');
      }
      if (isMbRoute) {
        if (!isMediaBlastUser) {
          return this.router.parseUrl('/notDesktop');
        }
        return this.router.parseUrl(mapMbSharedSubPath(state.url, '/media-blast', '/media-blast-mobile') || '/media-blast-mobile');
      }
      // The first-phone 2FA setup page also works on phones.
      if (isUpgradeLogin) {
        return true;
      }
      return this.router.parseUrl('/notDesktop');
    };
    const waitForAuthIfNeeded = (): Observable<boolean | UrlTree> => {
      // On mobile, decide() reads isMediaBlastUser for desktop MB routes too; wait for the
      // advertiser to load so a device switch does not misfire the '/notDesktop' redirect.
      if ((isMobileMbRoute || (isMobile && isMbRoute)) && this.mediaService.isAuthenticationInProcess
        && !this.mediaService.userAdvertiser && !this.mediaService.selectedAdvertiser) {
        return this.authService.onAuthenticationEnd.pipe(
          take(1),
          map(() => decide())
        );
      }
      return new Observable<boolean | UrlTree>(subscriber => {
        subscriber.next(decide());
        subscriber.complete();
      });
    };
    return this.authService.validate(urlTree.toString()).pipe(
      switchMap((valid) => {
        // A temporary token (2FA not passed) opens only the first-phone setup page.
        if (!valid || (this.mediaService.isLoginPending && !isUpgradeLogin)) {
          return new Observable<boolean | UrlTree>(subscriber => {
            subscriber.next(unauthRedirect());
            subscriber.complete();
          });
        }
        return waitForAuthIfNeeded();
      })
    );
  }
}

export const AuthenticationGuard: CanActivateFn = (next: ActivatedRouteSnapshot, state: RouterStateSnapshot):
  Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree => {
  return inject(AuthenticationGuardService).canActivate(next, state);
};
